if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLFormElement {
      constructor () {
        super()

        this.els = {
          form: this.querySelector('form'),
          addToCartButton: this.querySelector('[data-product-form-el="add-to-cart-button"]'),
          backInStockButton: this.querySelector('[data-product-form-el="back-in-stock-button"]'),
          variantIdInput: this.querySelectorAll('[data-product-form-el="variant-id-input"]'),
          optionRadios: this.querySelectorAll('[data-product-form-el="option-radio"]'),
          optionFieldSets: this.querySelectorAll('[data-product-form-el="option-fieldset"]'),
          stickyButton: this.querySelector('[data-product-form-el="sticky-button"]'),
          additionalCheckoutButtons: this.querySelector('[data-product-form-el="checkoutButtons"]'),
        }

        this.variantJSON = JSON.parse(
          this.querySelector('[data-product-form-el="variant-json"]').textContent,
        )

        this.state = {
          selectedOptions: this.buildSelectedOptions(),
        }
      }

      connectedCallback () {
        this.setupListeners()
        this.setupObservers()
        this.setupIntersectionObserver()
      }

      setupListeners () {
        this.addEventListener('change', this.handleChange)
        this.addEventListener('submit', this.handleSubmit, true)
        this.els?.stickyButton?.addEventListener('click', this.handleScrollIntoView.bind(this))
        window.addEventListener('pushcart:updated', this.toggleCheckoutButtons.bind(this))
      }

      handleSubmit (event) {
        const recipientForm = this.querySelector('recipient-form')

        if (!recipientForm || typeof recipientForm.validateSubmission !== 'function') {
          return
        }

        if (!recipientForm.validateSubmission()) {
          event.preventDefault()
          event.stopImmediatePropagation()
        }
      }

      toggleCheckoutButtons (e) {
        if (!this.els.additionalCheckoutButtons) {
          return
        }

        if (e.detail.itemCount > 0) {
          this.els.additionalCheckoutButtons.classList.add('is-hidden')
        } else {
          this.els.additionalCheckoutButtons.classList.remove('is-hidden')
        }
      }

      setupObservers () {
        if (this.hasAttribute('data-purple-dot-enabled-form')) {
          this.observeAddToCartButton()
        }
      }

      setupIntersectionObserver () {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.els.stickyButton?.classList.remove('prd-ProductOffers_ScrollToTop-show')
            } else {
              this.els.stickyButton?.classList.add('prd-ProductOffers_ScrollToTop-show')
            }
          })
        })

        observer.observe(this)
      }

      handleScrollIntoView () {
        this.scrollIntoView({ behavior: 'instant', block: 'start' })
      }

      async handleChange (event) {
        if (event.target && (event.target.type === 'radio' || event.target.tagName === 'SELECT')) {
          let selectedOption, optionName, optionValue

          if (event.target.type === 'radio') {
            selectedOption = event.target
            optionName = selectedOption.dataset.optionName
            optionValue = selectedOption.dataset.optionValue
          } else if (event.target.tagName === 'SELECT') {
            selectedOption = event.target
            optionName = selectedOption.dataset.optionName
            const selectedOptionElement = selectedOption.options[selectedOption.selectedIndex]
            optionValue = selectedOptionElement.dataset.optionValue
          }

          this.state.selectedOptions[optionName].value = optionValue

          const matchedVariant = this.variantJSON.variants.find(variant => {
            return (
              (this.state.selectedOptions.option1.value
                ? variant.option1 === this.state.selectedOptions.option1.value
                : true) &&
              (this.state.selectedOptions?.option2?.value
                ? variant.option2 === this.state.selectedOptions?.option2.value
                : true) &&
              (this.state.selectedOptions?.option3?.value
                ? variant.option3 === this.state.selectedOptions?.option3?.value
                : true)
            )
          })

          const url = new URL(window.location.href)
          url.searchParams.set('variant', matchedVariant.id)
          window.history.replaceState({}, '', url)

          const html = await window.BAOv2.utils.sectionRenderer.fetchSingle(
            'product-form',
            window.location.href,
          )

          const parser = new DOMParser()

          const doc = parser.parseFromString(html, 'text/html')

          const newForm = doc.querySelector('form')

          const newFormOptions = newForm.querySelector('.prd-Offer_Options')
          const currentFormOptions = this.querySelector('.prd-Offer_Options')

          currentFormOptions.innerHTML = newFormOptions.innerHTML

          const newVariantIdInput = newForm.querySelector(
            '[data-product-form-el="variant-id-input"]',
          )
          const variantIdInput = this.querySelector('[data-product-form-el="variant-id-input"]')

          variantIdInput.value = newVariantIdInput.value

          const newFormFooter = newForm.querySelector('.prd-ProductForm_Footer')
          const currentFormFooter = this.querySelector('.prd-ProductForm_Footer')

          if (this.hasAttribute('data-purple-dot-enabled-form') === false) {
            currentFormFooter.innerHTML = newFormFooter.innerHTML
          }

          this.els.optionFieldSets = this.querySelectorAll(
            '[data-product-form-el="option-fieldset"]',
          )

          this.state.selectedOptions = this.buildSelectedOptions()

          this.render()
        }
      }

      render () {
        this.els.optionFieldSets.forEach(optionFieldSet => {
          this.updateLegend(optionFieldSet)
        })
      }

      buildSelectedOptions () {
        const selectedOptions = {}
        this.els.optionFieldSets.forEach(optionFieldSet => {
          const selectedRadio = optionFieldSet.querySelector('input:checked')
          if (selectedRadio) {
            selectedOptions[selectedRadio.dataset.optionName] = {
              value: selectedRadio.dataset.optionValue,
              element: selectedRadio,
            }
          } else {
            const selectElement = optionFieldSet.querySelector('select')
            if (selectElement) {
              const selectedOption = selectElement.options[selectElement.selectedIndex]
              if (selectedOption && selectElement.dataset.optionName) {
                selectedOptions[selectElement.dataset.optionName] = {
                  value: selectedOption.dataset.optionValue,
                  element: selectElement,
                }
              }
            }
          }
        })
        return selectedOptions
      }

      updateLegend (optionFieldSet) {
        const legend = optionFieldSet.querySelector('[data-product-form-el="option-legend"]')
        if (!legend) return

        const legendSpan = legend.querySelector('span')
        if (!legendSpan) return

        const selectedRadio = optionFieldSet.querySelector('input:checked')
        if (selectedRadio) {
          legendSpan.textContent = selectedRadio.dataset.optionValue
          return
        }

        const selectElement = optionFieldSet.querySelector('select')
        if (selectElement) {
          const selectedOption = selectElement.options[selectElement.selectedIndex]
          if (selectedOption) {
            legendSpan.textContent = selectedOption.dataset.optionValue
          }
        }
      }

      findSelectedVariant () {
        return this.variantJSON.variants.find(variant => {
          return Object.keys(this.state.selectedOptions).every(optionName => {
            return variant[optionName] === this.state.selectedOptions[optionName].value
          })
        })
      }

      observeAddToCartButton () {
        const addToCartButton = this.els.addToCartButton

        const observer = new MutationObserver(mutationsList => {
          const currentForm = document.querySelector('[data-purple-dot-enabled-form]')
          const backInStockButton = this.els.backInStockButton

          if (backInStockButton && currentForm) {
            const backInStockEls = [
              backInStockButton.querySelector('[data-back-in-stock-el="trigger"]'),
              backInStockButton.querySelector('[data-back-in-stock-el="select"]'),
              backInStockButton.querySelector('[data-back-in-stock-el="email"]'),
              backInStockButton.querySelector('[data-back-in-stock-el="submit"]'),
            ]

            const shouldDisable =
              currentForm.classList.contains('x-pd-atc-form--instock') ||
              currentForm.classList.contains('x-pd-atc-form--preorder')

            backInStockEls.forEach(el => {
              if (el) el.disabled = shouldDisable
            })
          }
        })

        observer.observe(addToCartButton, { attributes: true })
      }
    },
    {
      extends: 'form',
    },
  )
}
